import { calcSaldoValoradoLinea } from './exportRetailData';

export const EXPORT_PRESETS = {
  essential: 'essential',
  all: 'all',
  none: 'none',
  custom: 'custom',
};

const col = (key, label, opts = {}) => ({
  key,
  label,
  group: opts.group || 'General',
  essential: opts.essential !== false,
  order: opts.order ?? 999,
  getValue: opts.getValue,
});

export const INVENTARIO_COLUMNS = [
  col('nombre', 'Producto', { group: 'Producto', order: 10 }),
  col('producto_base', 'Producto base', { group: 'Producto', essential: false, order: 15 }),
  col('marca', 'Marca', { group: 'Producto', order: 20 }),
  col('sku', 'SKU', { group: 'Identificación', order: 30 }),
  col('codigo_barras', 'Código de barras', { group: 'Identificación', order: 35 }),
  col('categoria', 'Categoría', { group: 'Clasificación', order: 40 }),
  col('temporada', 'Temporada', { group: 'Clasificación', essential: false, order: 45 }),
  col('talla', 'Talla', { group: 'Variante', order: 50 }),
  col('color', 'Color', { group: 'Variante', order: 55 }),
  col('corte', 'Corte', { group: 'Variante', essential: false, order: 60 }),
  col('stock', 'Stock', { group: 'Inventario', order: 70 }),
  col('saldo_valorado', 'Saldo valorado', {
    group: 'Montos',
    order: 71,
    getValue: (p, ctx) => calcSaldoValoradoLinea(p, ctx.store).toFixed(2),
  }),
  col('stock_reservado', 'Stock reservado', { group: 'Inventario', essential: false, order: 75 }),
  col('mostrarEnCatalogo', 'En catálogo', {
    group: 'Estado',
    essential: false,
    order: 180,
    getValue: (p) => (p.mostrarEnCatalogo ? 'Sí' : 'No'),
  }),
  col('activo', 'Activo', {
    group: 'Estado',
    essential: false,
    order: 190,
    getValue: (p) => (p.activo !== false ? 'Sí' : 'No'),
  }),
  col('costo_promedio', 'Costo promedio', {
    group: 'Montos',
    order: 100,
    getValue: (p, ctx) => {
      const v = ctx.store?.obtenerCostoPromedioProducto?.(p.id);
      return v != null ? Number(v).toFixed(2) : '';
    },
  }),
  col('precio_compra', 'Precio compra', { group: 'Montos', essential: false, order: 110 }),
  col('precio_venta', 'Precio venta', { group: 'Montos', order: 120 }),
  col('precio_minimo', 'Precio mínimo', { group: 'Montos', essential: false, order: 130 }),
  col('fecha_creacion', 'Fecha creación', { group: 'Sistema', essential: false, order: 200 }),
];

export const VENTAS_HEADER_COLUMNS = [
  col('fecha', 'Fecha', { group: 'Venta', order: 10 }),
  col('id', 'ID venta', { group: 'Venta', essential: false, order: 15 }),
  col('metodo_pago', 'Método de pago', { group: 'Venta', order: 20 }),
  col('metodo_pago_detalle', 'Detalle pago mixto', {
    group: 'Venta',
    essential: false,
    order: 25,
    getValue: (v) => {
      if (v.metodo_pago !== 'MIXTO' || !v.metodo_pago_detalle) return '';
      const d = v.metodo_pago_detalle;
      return `Efectivo: ${d.efectivo || 0} | QR: ${d.qr || 0}`;
    },
  }),
  col('canal', 'Canal', { group: 'Venta', essential: false, order: 30 }),
  col('codigo_campana', 'Código campaña', { group: 'Venta', essential: false, order: 35 }),
  col('cliente_id', 'ID cliente', { group: 'Venta', essential: false, order: 40 }),
  col('cantidad_items', 'Cantidad de ítems', {
    group: 'Venta',
    essential: false,
    order: 50,
    getValue: (v, ctx) => ctx.detalles?.length || 0,
  }),
  col('productos_resumen', 'Productos', {
    group: 'Venta',
    essential: false,
    order: 55,
    getValue: (v, ctx) =>
      (ctx.detalles || [])
        .map((d) => `${d.nombre} (x${d.cantidad})`)
        .join('; '),
  }),
  col('subtotal', 'Subtotal', { group: 'Montos', order: 100 }),
  col('descuento_total', 'Descuento', { group: 'Montos', order: 110 }),
  col('total', 'Total neto', { group: 'Montos', order: 120 }),
  col('costo_total', 'Costo total', { group: 'Montos', essential: false, order: 130 }),
  col('margen_bruto', 'Margen bruto', { group: 'Montos', essential: false, order: 140 }),
  col('anulado', 'Anulada', {
    group: 'Estado',
    order: 200,
    getValue: (v) => (v.anulado ? 'Sí' : 'No'),
  }),
];

export const VENTAS_LINE_COLUMNS = [
  col('fecha', 'Fecha', { group: 'Venta', order: 10 }),
  col('id', 'ID venta', { group: 'Venta', essential: false, order: 15 }),
  col('metodo_pago', 'Método de pago', { group: 'Venta', order: 20 }),
  col('producto_id', 'ID producto', { group: 'Producto', essential: false, order: 30 }),
  col('nombre', 'Producto', { group: 'Producto', order: 40 }),
  col('marca', 'Marca', { group: 'Producto', essential: false, order: 45 }),
  col('sku', 'SKU', { group: 'Producto', essential: false, order: 50 }),
  col('codigo_barras', 'Código', { group: 'Producto', order: 55 }),
  col('categoria', 'Categoría', { group: 'Producto', essential: false, order: 60 }),
  col('talla', 'Talla', { group: 'Producto', essential: false, order: 65 }),
  col('color', 'Color', { group: 'Producto', essential: false, order: 70 }),
  col('cantidad', 'Cantidad', { group: 'Línea', order: 80 }),
  col('precio_unitario', 'Precio unitario', { group: 'Montos línea', order: 90 }),
  col('costo_unitario', 'Costo unitario', { group: 'Montos línea', essential: false, order: 95 }),
  col('subtotal_linea', 'Subtotal línea', {
    group: 'Montos línea',
    order: 100,
    getValue: (_, ctx) => {
      const d = ctx.line;
      const qty = parseInt(d?.cantidad, 10) || 0;
      const price = parseFloat(d?.precio_unitario) || 0;
      return (qty * price).toFixed(2);
    },
  }),
  col('subtotal', 'Subtotal venta', { group: 'Montos venta', order: 110 }),
  col('descuento_total', 'Descuento venta', { group: 'Montos venta', order: 120 }),
  col('total', 'Total neto venta', { group: 'Montos venta', order: 130 }),
  col('anulado', 'Anulada', {
    group: 'Estado',
    order: 200,
    getValue: (v) => (v.anulado ? 'Sí' : 'No'),
  }),
];

export const COMPRAS_HEADER_COLUMNS = [
  col('fecha', 'Fecha', { group: 'Compra', order: 10 }),
  col('id', 'ID compra', { group: 'Compra', essential: false, order: 15 }),
  col('proveedor', 'Proveedor', { group: 'Compra', order: 20 }),
  col('cantidad_items', 'Cantidad de ítems', {
    group: 'Compra',
    essential: false,
    order: 30,
    getValue: (_, ctx) => ctx.detalles?.length || 0,
  }),
  col('productos_resumen', 'Productos', {
    group: 'Compra',
    essential: false,
    order: 35,
    getValue: (_, ctx) =>
      (ctx.detalles || [])
        .map((d) => `${d.nombre} (x${d.cantidad})`)
        .join('; '),
  }),
  col('total', 'Total', { group: 'Montos', order: 120 }),
];

export const COMPRAS_LINE_COLUMNS = [
  col('fecha', 'Fecha', { group: 'Compra', order: 10 }),
  col('id', 'ID compra', { group: 'Compra', essential: false, order: 15 }),
  col('proveedor', 'Proveedor', { group: 'Compra', order: 20 }),
  col('producto_id', 'ID producto', { group: 'Producto', essential: false, order: 30 }),
  col('nombre', 'Producto', { group: 'Producto', order: 40 }),
  col('codigo_barras', 'Código', { group: 'Producto', essential: false, order: 50 }),
  col('cantidad', 'Cantidad', { group: 'Línea', order: 60 }),
  col('precio_compra', 'Precio compra', { group: 'Montos línea', order: 90 }),
  col('subtotal_linea', 'Subtotal línea', {
    group: 'Montos línea',
    order: 100,
    getValue: (_, ctx) => {
      const d = ctx.line;
      const qty = parseInt(d?.cantidad, 10) || 0;
      const price = parseFloat(d?.precio_compra) || 0;
      return (qty * price).toFixed(2);
    },
  }),
  col('total', 'Total compra', { group: 'Montos compra', order: 120 }),
];

export const DATASET_CONFIG = {
  inventario: {
    title: 'Inventario',
    filename: 'inventario',
    columns: INVENTARIO_COLUMNS,
    modes: null,
    defaultMode: null,
  },
  ventas: {
    title: 'Ventas',
    filename: 'ventas',
    columnsByMode: {
      header: VENTAS_HEADER_COLUMNS,
      detail: VENTAS_LINE_COLUMNS,
    },
    defaultMode: 'detail',
    modes: [
      { key: 'detail', label: 'Detalle', description: '1 fila por producto vendido' },
      { key: 'header', label: 'Resumen', description: '1 fila por venta' },
    ],
  },
  compras: {
    title: 'Compras',
    filename: 'compras',
    columnsByMode: {
      header: COMPRAS_HEADER_COLUMNS,
      detail: COMPRAS_LINE_COLUMNS,
    },
    defaultMode: 'detail',
    modes: [
      { key: 'detail', label: 'Detalle', description: '1 fila por producto comprado' },
      { key: 'header', label: 'Resumen', description: '1 fila por compra' },
    ],
  },
};

export function getColumnsForDataset(dataset, mode = 'detail') {
  const config = DATASET_CONFIG[dataset];
  if (!config) return [];
  if (config.columns) return sortColumnsByOrder(config.columns);
  const cols = config.columnsByMode?.[mode] || config.columnsByMode?.detail || config.columnsByMode?.header || [];
  return sortColumnsByOrder(cols);
}

export function getDefaultExportMode(dataset) {
  const config = DATASET_CONFIG[dataset];
  if (!config?.modes) return 'header';
  return config.defaultMode || config.modes[0]?.key || 'detail';
}

export function sortColumnsByOrder(columns) {
  return [...columns].sort((a, b) => a.order - b.order);
}

export function sortKeysByColumnOrder(keys, columns) {
  const orderMap = new Map(columns.map((c) => [c.key, c.order]));
  return [...keys].sort((a, b) => (orderMap.get(a) ?? 999) - (orderMap.get(b) ?? 999));
}

export function getDefaultOrderedKeys(columns, preset = EXPORT_PRESETS.essential) {
  const sorted = sortColumnsByOrder(columns);
  if (preset === EXPORT_PRESETS.all) return sorted.map((c) => c.key);
  if (preset === EXPORT_PRESETS.none) return [];
  return sorted.filter((c) => c.essential).map((c) => c.key);
}

/** @deprecated use getDefaultOrderedKeys */
export function getDefaultSelectedKeys(columns, preset) {
  return getDefaultOrderedKeys(columns, preset);
}

export function insertKeyInLogicalOrder(orderedKeys, newKey, columns) {
  if (orderedKeys.includes(newKey)) return orderedKeys;
  return sortKeysByColumnOrder([...orderedKeys, newKey], columns);
}

export function groupColumns(columns) {
  const groups = {};
  sortColumnsByOrder(columns).forEach((colDef) => {
    if (!groups[colDef.group]) groups[colDef.group] = [];
    groups[colDef.group].push(colDef);
  });
  return groups;
}

export function getColumnLabel(columns, key) {
  return columns.find((c) => c.key === key)?.label || key;
}
